import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Clock,
  CheckCircle2,
  Circle,
  Building2,
  Sparkles,
  Euro,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProjectWithStats } from './ProjectsTable';

interface ProjectCardProps {
  project: ProjectWithStats;
}

const statusConfig = {
  'not-started': {
    label: 'Not Started',
    icon: Circle,
    className: 'bg-muted text-muted-foreground',
  },
  'in-progress': {
    label: 'In Progress',
    icon: Clock,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
};

const typeConfig = {
  client: {
    label: 'Client Project',
    icon: Building2,
    gradient: 'from-blue-500 to-blue-600',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  'side-project': {
    label: 'Side Project',
    icon: Sparkles,
    gradient: 'from-purple-500 to-purple-600',
    badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
};

export function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate();
  const statusConf = statusConfig[project.status];
  const typeConf = typeConfig[project.type];
  const TypeIcon = typeConf.icon;

  const taskProgress =
    project.totalTasks > 0
      ? Math.round((project.completedTasks / project.totalTasks) * 100)
      : 0;

  return (
    <Card
      className="border border-border hover:bg-muted/50 transition-colors cursor-pointer group relative overflow-hidden"
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <CardContent className="p-5">
        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <Badge className={statusConf.className} variant="outline">
            {statusConf.label}
          </Badge>
        </div>

        {/* Project Header */}
        <div className="flex items-start gap-4 mb-6">
          <div
            className={cn(
              'w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shadow-lg',
              typeConf.gradient
            )}
          >
            <TypeIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0 pr-16">
            <h3 className="font-bold text-lg text-foreground group-hover:text-primary transition-colors truncate">
              {project.name}
            </h3>
            {project.clientName && (
              <p className="text-sm text-muted-foreground mt-1">{project.clientName}</p>
            )}
            <div className="mt-2">
              <Badge className={typeConf.badge} variant="outline">
                {typeConf.label}
              </Badge>
            </div>
          </div>
        </div>

        {/* Project Stats */}
        <div className="space-y-4 mb-4">
          {/* Task Progress */}
          <div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Tasks</span>
              <span className="font-medium text-foreground">
                {project.completedTasks}/{project.totalTasks}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${taskProgress}%` }}
              />
            </div>
          </div>

          {/* Hours and Earnings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted/50 dark:bg-muted/20 rounded-lg">
              <div className="flex items-center justify-center mb-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground mb-1">Hours</p>
              <p className="font-bold text-foreground">{project.totalHours}h</p>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-center mb-1">
                <Euro className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-xs text-muted-foreground mb-1">Earnings</p>
              <p className="font-bold text-green-600 dark:text-green-400">
                €{project.earnings.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Notes Preview */}
        {project.notes && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {project.notes}
          </p>
        )}

        {/* Quick Actions (appear on hover) */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-card via-card to-transparent p-4 translate-y-full group-hover:translate-y-0 transition-transform">
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${project.id}`);
              }}
            >
              View Details
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${project.id}/edit`);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ProjectsCardGridProps {
  projects: ProjectWithStats[];
  emptyState?: React.ReactNode;
}

export function ProjectsCardGrid({ projects, emptyState }: ProjectsCardGridProps) {
  if (projects.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {projects.map((project) => (
        <ProjectCard key={project.id} project={project} />
      ))}
    </div>
  );
}
